import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import { useTranslation } from 'react-i18next';
import {
  acceptAllCookies,
  readCookiePreferences,
  rejectOptionalCookies,
} from '../../privacy/cookieConsent';
import CookiePreferencesPanel from './CookiePreferencesPanel';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    setVisible(!readCookiePreferences());
  }, []);

  const hideWith = (action) => {
    action();
    setVisible(false);
    setCustomizing(false);
  };

  if (!visible) return null;

  return (
    <>
      <Paper
        elevation={8}
        role="region"
        aria-label={t('cookies.banner.title')}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: { xs: 12, md: 24 },
          right: { xs: 12, md: 24 },
          bottom: { xs: 88, md: 24 },
          maxWidth: 960,
          mx: 'auto',
          p: { xs: 2, md: 2.5 },
          border: '1px solid',
          borderColor: 'rgba(15, 76, 69, 0.18)',
          borderRadius: 2,
          bgcolor: 'rgba(255, 250, 240, 0.96)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: 'rgba(15, 76, 69, 0.1)',
                color: 'primary.main',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <CookieOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h4">{t('cookies.banner.title')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('cookies.banner.copy')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => hideWith(acceptAllCookies)}>
              {t('cookies.actions.accept')}
            </Button>
            <Button variant="outlined" onClick={() => hideWith(rejectOptionalCookies)}>
              {t('cookies.actions.reject')}
            </Button>
            <Button variant="text" onClick={() => setCustomizing(true)}>
              {t('cookies.actions.customize')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={customizing} onClose={() => setCustomizing(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('cookies.preferences.title')}</DialogTitle>
        <DialogContent>
          <CookiePreferencesPanel
            compact
            onSaved={() => {
              setVisible(false);
              setCustomizing(false);
            }}
          />
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            fullWidth
            onClick={() => setCustomizing(false)}
          >
            {t('common.close')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
